import React, { ComponentType } from 'react';
import { Box } from '@mui/material';
import {
  Scheduler,
  DayView,
  WeekView,
  Appointments,
} from '@devexpress/dx-react-scheduler-material-ui';
import { ViewState } from '@devexpress/dx-react-scheduler';
import useOrganizationStartWeekDay from 'hooks/useOrganizationStartWeekDay';
import { Schedule } from 'store/schedules/interfaces';
import useScrollCalendar from 'hooks/useScrollCalendar';
import TimeScaleTickCellComponent from 'components/BookProperties/BookingForm/MinutesBookingScheduler/WeekView/TimeScaleTickCellComponent/TimeScaleTickCellComponent';
import TimeScaleLabelComponent from 'components/ScheduleCalendar/libComponents/TimeScaleLabelComponent';
import getEarliestScheduleShift from 'components/ScheduleCalendar/getEarliestScheduleShift';
import { cellDuration } from 'components/AddWorkSchedule/WorkScheduleCalendar/consts';
import SchedulePreviewCalendarProvider from './context/SchedulePreviewCalendarProvider';
import ScheduleSummary from './ScheduleSummary/ScheduleSummary';
import DayScaleCellComponent from './libComponents/DayScaleCellComponent/DayScaleCellComponent';
import TimeTableCellComponent from './libComponents/TimeTableCellComponents';
import AppointmentContainerComponent from './libComponents/AppointmentContainerComponent';
import AppointmentContentComponent from './libComponents/AppointmentContentComponent';
import { PreviewScheduleRange } from './convertScheduleRanges';

interface SchedulePreviewCalendarProps {
  schedule: Schedule;
  scheduleRanges: PreviewScheduleRange[];
}

export default function SchedulePreviewCalendar({
  schedule,
  scheduleRanges,
}: SchedulePreviewCalendarProps) {
  const earliestScheduleShift = getEarliestScheduleShift(
    schedule.scheduleRanges,
  );
  const schedulerWrapperRef = useScrollCalendar({
    scrollHour: earliestScheduleShift,
  });
  const startWeekDay = useOrganizationStartWeekDay();

  const assignedEmployeesCount = schedule.employees.length;
  const weekIntervalCount = schedule.numberOfDays / 7;

  return (
    <>
      <Box
        height="calc(100vh - 24.6rem)"
        sx={(theme) => ({
          borderBottom: `0.1rem solid ${theme.palette.border.main}`,
        })}
        className="scheduler-wrapper"
        ref={schedulerWrapperRef}
      >
        <SchedulePreviewCalendarProvider
          scheduleRanges={scheduleRanges}
          schedule={schedule}
        >
          <Scheduler
            data={scheduleRanges}
            firstDayOfWeek={startWeekDay}
            height="auto"
          >
            <ViewState />
            {schedule.regular ? (
              <WeekView
                dayScaleCellComponent={DayScaleCellComponent}
                timeTableCellComponent={TimeTableCellComponent}
                timeScaleTickCellComponent={TimeScaleTickCellComponent}
                timeScaleLabelComponent={TimeScaleLabelComponent}
                cellDuration={cellDuration}
                intervalCount={weekIntervalCount}
              />
            ) : (
              <DayView
                dayScaleCellComponent={DayScaleCellComponent}
                timeTableCellComponent={TimeTableCellComponent}
                timeScaleTickCellComponent={TimeScaleTickCellComponent}
                timeScaleLabelComponent={TimeScaleLabelComponent}
                cellDuration={cellDuration}
                intervalCount={schedule.numberOfDays}
              />
            )}
            <Appointments
              appointmentContentComponent={
                AppointmentContentComponent as ComponentType<Appointments.AppointmentContentProps>
              }
              containerComponent={
                AppointmentContainerComponent as ComponentType<Appointments.ContainerProps>
              }
            />
          </Scheduler>
        </SchedulePreviewCalendarProvider>
      </Box>
      <ScheduleSummary
        scheduleRanges={scheduleRanges}
        assignedEmployeesCount={assignedEmployeesCount}
      />
    </>
  );
}
